import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { KeyRound, Lock, X } from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// ── Design tokens (matches Add/Edit Staff / Leads / Members) ──────────────────
const C = {
  teal: '#115550',
  tealMid: '#16706a',
  tealLight: '#c6e3e0',
  lime: '#c8e94b',
  limeDim: 'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white: '#ffffff',
  muted: '#9ca3af',
  sub: '#6b7280',
};
const G = {
  shellBg: '#f8f9f9',
  shellBorder: '#e8ebeb',
  line: '#e5e7eb',
  soft: '#eef0f0',
};
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Types ─────────────────────────────────────────────────────────────────────
export interface ResettableStaff {
  id: string;
  email: string | null;
  name: string | null;
}

interface ResetPasswordModalProps {
  open: boolean;
  staff: ResettableStaff | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  password: string;
  confirmPassword: string;
}

const EMPTY: FormData = { password: '', confirmPassword: '' };

// ── Small building blocks ────────────────────────────────────────────────────
const iconBox = (size = 29.5): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '7.4px',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: C.lime,
});

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '9.2px',
      marginBottom: '13px',
      paddingBottom: '11px',
      borderBottom: `1px solid ${G.soft}`,
    }}
  >
    <div style={iconBox()}>{icon}</div>
    <span
      style={{
        color: C.teal,
        fontWeight: 700,
        fontSize: '0.71rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ open, staff, onClose, onSuccess }) => {
  const toast = useRef<Toast>(null);

  const [formData, setFormData] = useState<FormData>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) setFormData(EMPTY);
  }, [open]);

  const update = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const showError = (detail: string, summary = 'Validation Error') =>
    toast.current?.show({ severity: 'error', summary, detail, life: 5000 });

  const handleSubmit = async () => {
    if (!staff) return;

    if (!formData.password) return showError('New password is required');
    // Backend binding: min=6
    if (formData.password.length < 6) return showError('Password must be at least 6 characters');
    if (formData.password !== formData.confirmPassword) return showError('Passwords do not match');

    setLoading(true);
    try {
      // PATCH /api/v1/staff/:id/reset-password
      await apiClient.patch(`/v1/staff/${staff.id}/reset-password`, {
        password: formData.password,
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Password reset successfully!',
        life: 4000,
      });
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 300);
    } catch (e: any) {
      showError(
        e.response?.data?.error || e.response?.data?.message || 'Failed to reset password',
        'Error'
      );
    } finally {
      setLoading(false);
    }
  };

  const staffLabel = staff ? (staff.name && staff.name.trim()) || staff.email?.split('@')[0] || 'this member' : '';

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          ...iconBox(44),
          borderRadius: '12px',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <KeyRound size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: '1.01rem',
            fontWeight: 700,
            color: '#111827',
            lineHeight: 1.2,
            marginBottom: '3px',
          }}
        >
          Reset Password
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          For {staffLabel}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className={`reset-pwd-dialog ${isClosing ? 'reset-pwd-dialog-closing' : ''}`}
        maskClassName={`reset-pwd-mask ${isClosing ? 'reset-pwd-mask-closing' : ''}`}
        style={{ width: '90vw', maxWidth: '480px' }}
        modal
        dismissableMask={!loading}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .reset-pwd-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: resetPwdMaskIn 0.3s ease; }
          .reset-pwd-mask-closing { animation: resetPwdMaskOut 0.3s ease forwards !important; }
          .reset-pwd-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: resetPwdDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
          .reset-pwd-dialog-closing.p-dialog { animation: resetPwdDlgOut 0.3s cubic-bezier(0.4,0,0.2,1) forwards !important; }
          .reset-pwd-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
          .reset-pwd-dialog .p-dialog-header-icon { color: ${C.sub}; }
          .reset-pwd-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
          @keyframes resetPwdMaskIn  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes resetPwdMaskOut { from { opacity: 1; } to { opacity: 0; } }
          @keyframes resetPwdDlgIn  { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes resetPwdDlgOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.92) translateY(16px); } }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<Lock size={14} />} title="New Password" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                <GlobalInputField
                  label="New Password"
                  required
                  type="password"
                  icon={Lock}
                  value={formData.password}
                  onChange={update('password')}
                  placeholder="Minimum 6 characters"
                  disabled={loading}
                  autoComplete="new-password"
                />
                <GlobalInputField
                  label="Confirm Password"
                  required
                  type="password"
                  icon={Lock}
                  value={formData.confirmPassword}
                  onChange={update('confirmPassword')}
                  placeholder="Re-enter password"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '7.4px',
              marginTop: '2px',
            }}
          >
            <GlobalButton
              label="Cancel"
              variant="secondary"
              icon={X}
              onClick={handleClose}
              disabled={loading}
            />
            <GlobalButton
              label={loading ? 'RESETTING...' : 'RESET PASSWORD'}
              variant="primary"
              icon={KeyRound}
              onClick={handleSubmit}
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ResetPasswordModal;
